<template>
  <div>
    <navigation />
    <div class="page-body">
    <div class="experience-switch" v-if="!bannerDismissed && proof.id > 0">
      <div class="experience-msg">
        <span class="experience-pill">New</span>
        <span class="experience-text">You're using the new proof details.</span>
      </div>
      <div class="experience-actions">
        <button
          type="button"
          class="experience-link experience-link-btn"
          v-b-modal="'feedbackModal-newProofDetails'"
        >Give feedback</button>
        <span class="experience-divider" aria-hidden="true">·</span>
        <a :href="`/proof-item?id=${proof.id}`" class="experience-link">Switch to classic →</a>
      </div>
      <button
        type="button"
        class="experience-close"
        @click="dismissBanner"
        aria-label="Dismiss banner"
        title="Dismiss"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>

    <feedbackModal
      name="feedbackModal-newProofDetails"
      campaign-key="new-proof-details-v1"
      title="How's the new proof details page?"
      subtitle="Your take helps us decide what to keep."
      scale-question="Compared to the classic experience, how would you rate this?"
      :scale-min="1"
      :scale-max="7"
      scale-low-label="Much worse"
      scale-high-label="Much better"
      text-prompt="What's working or not working?"
      text-placeholder="Tell us anything that helps."
      submit-label="Send feedback"
    />

    <proofDetails
      v-show="proof.id > 0"
      :proof.sync="proof"
      :user="user"
      :teams="teams"
      :all-users="allUsers"
      :active-user-ids="activeUserIds"
      v-on:exportTimeline="exportPdf($event)"
    />
    </div>

    <create-workflow-item :input-proof="proof" ref="workflowSidebar" />

    <confirmationModal
      body=""
      question=""
      confirm-button-text=""
      event-to-emit-on-confirmation=""
      name=""
      button-css=""
    />
    <modalEditContact />
    <modalAddNote :proof="proof" :cur-user="user" />
    <modalSendMessage :proof="proof" />
    <modalEditNonApprovers
      :account-users="proof.currentWorkflow.stages[0].bccUserIds"
    />
    <modalEditName
      :proof-name="proof.name"
      v-on:proof_name_update="handleNameChangeEvent($event)"
    />
    <modalEditTeam :proof="proof" />
    <modalEditDeadline />
    <modalEditOwner :users="allUsers" :proof="proof" />
    <modalSendNewVersion :proof="proof" :cur-user="user" />
    <modalRescheduleSend ref="rescheduleSendModal" :proof="proof" />
    <modalManageFiles
      :proof="proof"
      :cur-user="user"
      :proof-public-id="proof.publicId"
    />
    <modal-update-proof-options :proof="proof" :cur-user="user" />
    <archiveProofModal
      name="archiveProofModal"
      @confirmed="archiveProof"
      :proof-id="proof.id"
      question="Archive This Proof?"
      body="This proof will no longer be accessible to approvers and you will be unable to send new versions. You will have full access to files and timeline."
      confirm-button-text="Yes, Archive"
      :cur-user="user"
      :proof="proof"
    />
    <modal
      name="restoreProofModal"
      eventToEmitOnConfirmation="restore::proof::confirmed"
      question="Restore This Proof?"
      body="Restoring this proof will allow you to actively manage this proof, including new versions, contacts and workflows."
      confirm-button-text="Yes, Restore"
    />
    <modal
      name="deleteProofModal"
      eventToEmitOnConfirmation="delete::proof::confirmed"
      question="Delete This Proof?"
      body="Are you sure you want to delete this proof? You cannot undo this action."
      confirm-button-text="Yes, Delete"
    />
    <proofLink
      :proof-public-id="proof.publicId"
      :proof-reviewer-id="proof.reviewerPublicId"
    />
  </div>
</template>

<script>
import { setPageTitle } from "../utils/helpers";
import modalEditName from "../components/modals/modal-editname.vue";
import modalEditContact from "../components/modals/modal-editcontact.vue";
import proofDetails from "../components/proof-item/proof-details.vue";
import modalAddNote from "../components/modals/modal-addnote.vue";
import modalSendMessage from "../components/modals/modal-sendmessage.vue";
import modalEditNonApprovers from "../components/modals/modal-editnonapprover.vue";
import modalEditTeam from "../components/modals/modal-edit-team.vue";
import modalEditDeadline from "../components/modals/modal-editdeadline.vue";
import modalEditOwner from "../components/modals/modal-editowner.vue";
import modalSendNewVersion from "../components/modals/modal-sendnewversion.vue";
import modalManageFiles from "../components/modals/modal-manage-files";
import modalUpdateProofOptions from "../components/modals/modal-updated-proof-options";
import proofLink from "../components/modals/modal-proof-link.vue";
import confirmationModal from "../components/modals/modal-confirmation.vue";
import createWorkflowItem from "../components/automation-archive/create-workflow-item";
import archiveProofModal from "../components/modals/modal-archive-proof.vue";
import modalRescheduleSend from "../components/modals/modal-reschedule-send.vue";
import VueRandomColor from "vue-randomcolor";
import moment from "moment";
import Vue from "vue";

Vue.use(VueRandomColor);

export default {
  components: {
    modalEditName,
    modalEditContact,
    confirmationModal,
    proofDetails,
    modalAddNote,
    modalSendMessage,
    modalEditNonApprovers,
    modalEditTeam,
    modalEditDeadline,
    modalEditOwner,
    modalSendNewVersion,
    modalManageFiles,
    modalUpdateProofOptions,
    proofLink,
    createWorkflowItem,
    archiveProofModal,
    modalRescheduleSend,
  },
  data() {
    let dismissed = false;
    try {
      dismissed = window.localStorage.getItem("experience-banner::proof-details::dismissed") === "1";
    } catch (_e) { /* ignore */ }
    return {
      bannerDismissed: dismissed,
      user: this.$A.Auth || {},
      saveTask: null,
      fullyLoaded: false,
      proof: {
        id: 0,
        name: "",
        publicId: "",
        reviewerPublicId: "",
        currentVersion: {
          sender: { name: "" },
          versionFiles: [],
        },
        currentWorkflow: {
          stages: [{ bccUserIds: [] }],
        },
      },
      teams: [],
      realTimeConnection: null,
      activeUserInactivityInMinutes: 3,
      activeUserIds: {},
      allUsers: [],
    };
  },
  async mounted() {
    let self = this;
    let proofId = 0;

    if (this.$route.query.id !== undefined) {
      proofId = this.$route.query.id;
    } else if (
      this.$A.viewToViewData != null &&
      this.$A.viewToViewData.proof !== undefined &&
      this.$A.viewToViewData.proof !== null &&
      this.$A.viewToViewData.proof.id > 0
    ) {
      proofId = this.$A.viewToViewData.proof.id;
      this.$A.InsertUrlParam("id", proofId);
    }

    if (proofId > 0) {
      await this.loadProof(proofId);
      this.allUsers = (await this.$A.AccountService.All()) || [];

      this.$root.$on("proof::refresh", async function (proofMaybe) {
        if (proofMaybe && proofMaybe.proof) {
          self.proof = proofMaybe.proof;
        } else {
          await self.loadProof(self.proof.id);
        }
      });
      this.$root.$on("proof::tags::updated", async function (updatedTags) {
        self.proof.tags = updatedTags;
      });
      this.$root.$on("proof::custom-props::updated", async function () {
        await self.loadProof(self.proof.id);
      });
      this.$root.$on("delete::proof::confirmed", async function () {
        await self.$A.ProofService.Delete(self.proof.id);
        self.$A.NavigateToView("/proof-list", {});
      });
      this.$root.$on("restore::proof::confirmed", async function () {
        await self.$A.ProofService.Restore(self.proof.id);
        await self.loadProof(proofId);
      });
      this.$root.$on("proof::save::dueDate", async function (obj) {
        self.proof.dueDate = obj.dueDate;
        await self.saveProof();
      });
      this.$root.$on("proof::save::settings", async function (settings) {
        if (!self.fullyLoaded || settings === null) {
          return;
        }
        self.proof.allowApproveWithChanges = settings.allowApproveWithChanges;
        self.proof.allowDownload = settings.allowDownload;
        self.proof.allowApproveIndividualFiles = settings.allowApproveIndividualFiles;
        self.proof.allowViewAllComments = settings.allowViewAllComments;
        self.proof.limitVersionVisibility = settings.limitVersionVisibility;
        self.proof.blockCommentingAfterProofDecision = settings.blockCommentingAfterProofDecision;
        self.proof.watermarkEnabled = settings.watermarkEnabled;
        self.proof.password = settings.password || "";
        self.proof.removePassword = !!settings.removePassword;
        await self.saveProof();
      });
      this.$root.$on("proof::save", async function () {
        await self.saveProof();
      });
      this.$root.$on("proof::save::owner", async function (obj) {
        if (obj.senderId > 0) {
          let proof = await self.$A.ProofService.UpdateSender(
            self.proof.id,
            obj.senderId
          );
          if (proof && proof.id > 0) {
            self.proof = proof;
          }
        }
      });
      this.$root.$on("proof::save::team", async function (obj) {
        self.proof.usersTeamIds = obj.ashoreUsersTeamIds;
        await self.saveProof();
      });
    } else {
      setTimeout(function () {
        self.proof.name = "Proof Not Found";
      }, 1000);
    }

    this.teams = await this.$A.TeamService.GetAccountTeams();
  },
  watch: {
    "proof.name": function (name) {
      if (this.proof.id > 0) setPageTitle(`${name} (#${this.proof.id})`);
    },
  },
  methods: {
    dismissBanner() {
      this.bannerDismissed = true;
      try {
        window.localStorage.setItem("experience-banner::proof-details::dismissed", "1");
      } catch (_e) { /* ignore */ }
    },
    archiveProof: async function (args) {
      await this.$A.ProofService.Archive(this.proof.id, args.note);
      await this.loadProof(this.proof.id);
    },
    registerRealTimeConnection: async function () {
      let self = this;
      if (this.realTimeConnection !== null) return;
      this.realTimeConnection = this.$A.RealtimeService.Connect();

      this.realTimeConnection.on("ReadyForRegistration", async () => {
        const token = _.get(self, "$A.Auth.apiToken.token", "");
        const publicId = _.get(self, "proof.publicId", "");
        if (!token || !publicId) return;
        try {
          await self.realTimeConnection.invoke(
            "RegisterReviewerConnection",
            token,
            publicId,
            "proofPage"
          );
          await self.realTimeConnection.invoke(
            "LogLocalIdentity",
            JSON.stringify(window.$A.LoadDataCache("approver_keychain"))
          );
        } catch (err) {
          console.error(err);
        }
      });

      this.realTimeConnection.on("ReceiveProofEvents", async (appEvent) => {
        if (self.proof.proofEvents.find((e) => e.id === appEvent.id)) return;
        self.proof.proofEvents.unshift(appEvent);
        if (appEvent.eventType === 39) {
          const statusToLabel = {
            0: "Not Started",
            1: "Waiting For Review",
            2: "Waiting For Response",
            3: "Not Approved",
            4: "Approved",
          };
          const stage = self.proof.currentVersion.stages.find(
            (s) => appEvent.appEventObject.workflowStageId
          );
          stage.approvers
            .filter((a) => a.id === appEvent.appEventObject.approverId)
            .forEach((a) => {
              a.workflowStageApproverStatus =
                appEvent.appEventObject.proofApproverResponse.approvalStatus;
              a.workflowStageApproverStatusLabel =
                statusToLabel[a.workflowStageApproverStatus];
            });
          stage.reviewers
            .filter((a) => a.id === appEvent.appEventObject.approverId)
            .forEach((a) => {
              a.workflowStageApproverStatus =
                appEvent.appEventObject.proofApproverResponse.approvalStatus;
              a.workflowStageApproverStatusLabel =
                statusToLabel[a.workflowStageApproverStatus];
            });
        }
      });

      this.realTimeConnection.on("ReceiveUserCursorUpdate", () => {});
      this.realTimeConnection.on("PresenceUpdate", (users) => {
        let self = this;
        self.activeUserIds = {};
        users
          .filter((u) => self.proof != null && u.proofId === self.proof.id)
          .filter(
            (u) =>
              !u.lastHeartbeat ||
              moment(u.lastHeartbeat)
                .add(this.activeUserInactivityInMinutes, "minute")
                .isAfter(moment())
          )
          .forEach((u) => (self.activeUserIds[u.userId || u.approverId] = true));
      });

      this.realTimeConnection.start();
    },
    exportPdf: async function () {
      let self = this;
      let token = await this.$A.AccountService.GetTempToken();
      let url =
        this.$A.Env.apiHost +
        "/proof/" +
        self.proof.id +
        "/export-pdf?temp_access_token=" +
        token;
      let a = document.createElement("a");
      a.style.display = "none";
      document.body.appendChild(a);
      a.setAttribute("target", "_blank");
      a.setAttribute("href", url);
      a.setAttribute("download", self.proof.name + ".pdf");
      a.click();
      document.body.removeChild(a);
    },
    loadProof: async function (proofId) {
      let proof = proofId && (await this.$A.ProofService.GetFullProof(proofId));
      if (proof) {
        if (proof.id > 0) {
          this.proof = proof;
          let s = this;
          setTimeout(function () {
            s.fullyLoaded = true;
            s.registerRealTimeConnection();
          }, 100);
        } else if (proof.message !== undefined) {
          this.$A.AlertUser(proof.message, "warning", 20000);
        }
      }
    },
    reallySaveProof: async function () {
      if (this.proof.id > 0) {
        let saveProofObj = {
          name: this.proof.name,
          dueDate: this.proof.dueDate,
          password: this.proof.password,
          removePassword: this.proof.removePassword,
          allowDownload: this.proof.allowDownload,
          allowEdit: this.proof.allowEdit,
          allowApproveWithChanges: this.proof.allowApproveWithChanges,
          allowApproveIndividualFiles: this.proof.allowApproveIndividualFiles,
          allowViewAllComments: this.proof.allowViewAllComments,
          limitVersionVisibility: this.proof.limitVersionVisibility,
          blockCommentingAfterProofDecision:
            this.proof.blockCommentingAfterProofDecision,
          watermarkEnabled: this.proof.watermarkEnabled,
          tagIds: [],
          usersTeamIds: this.proof.usersTeamIds,
          templateWorkflowId: this.proof.templateWorkflowId,
        };
        let proof = await this.$A.ProofService.CreateUpdateProof(
          this.proof.id,
          saveProofObj
        );
        if (proof !== null && proof.id > 0) {
          this.proof = await this.$A.ProofService.GetFullProof(this.proof.id);
          this.$A.AlertUser("Proof Has Been Saved", "success");
        } else if (
          _.get(proof, "success") !== null &&
          !_.get(proof, "success")
        ) {
          const msg = _.get(
            proof,
            "message",
            "Proof Failed to Save. Please try again."
          );
          this.$A.AlertUser(msg);
        }
      }
    },
    handleNameChangeEvent: function ($event) {
      this.proof.name = $event.name;
      this.saveProof();
    },
    saveProof: async function () {
      if (!this.fullyLoaded) return;
      if (this.saveTask !== null) clearTimeout(this.saveTask);
      let s = this;
      this.saveTask = setTimeout(async function () {
        await s.reallySaveProof();
        s.saveTask = null;
      }, 1000);
    },
  },
};
</script>

<style scoped>
/* Wraps banner + proofDetails so the shell can flex to fill the
   remaining viewport whether the banner is shown or dismissed.
   Definite height (not min-height) so flex:1 children resolve. */
.page-body {
  display: flex;
  flex-direction: column;
  margin-top: 64px; /* clear position:fixed nav on desktop */
  height: calc(100vh - 64px);
}
@media (max-width: 991px) {
  .page-body {
    margin-top: 0;
    height: auto;
    min-height: 100vh;
  }
}

.experience-switch {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  column-gap: 16px;
  row-gap: 4px;
  padding: 8px 48px;
  background: rgba(1, 146, 86, 0.06);
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
  font-size: 13px;
  color: #4b5563;
  flex-shrink: 0;
}
.experience-msg,
.experience-actions {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}
.experience-pill {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  background: #019256;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  border-radius: 999px;
  letter-spacing: 0.02em;
}
.experience-text { color: #4b5563; }
.experience-link {
  color: #019256;
  font-weight: 600;
  text-decoration: none;
}
.experience-link:hover { text-decoration: underline; color: #017a48; }
.experience-link-btn {
  background: transparent;
  border: 0;
  padding: 0;
  font: inherit;
  cursor: pointer;
}
.experience-divider {
  color: #c2c8d0;
  font-weight: 600;
}
.experience-close {
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: transparent;
  border: 0;
  border-radius: 6px;
  color: #6b7280;
  cursor: pointer;
  transition: background-color 120ms ease, color 120ms ease;
}
.experience-close:hover {
  background: rgba(15, 23, 42, 0.06);
  color: #111827;
}
</style>
